import DOMPurify from 'dompurify';

const ALLOWED_TAGS = [
  'a', 'b', 'blockquote', 'br', 'code', 'del', 'div', 'em', 'h1', 'h2', 'h3',
  'h4', 'h5', 'h6', 'hr', 'i', 'img', 'li', 'ol', 'p', 'pre', 's', 'span',
  'strong', 'style', 'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'tr', 'u',
  'ul',
];

const ALLOWED_ATTR = [
  'align', 'alt', 'bgcolor', 'border', 'cellpadding', 'cellspacing', 'class',
  'colspan', 'height', 'href', 'rel', 'rowspan', 'scope', 'src', 'style',
  'target', 'title', 'valign', 'width',
];

const URI_SAFE_ATTR = [
  'align', 'bgcolor', 'border', 'cellpadding', 'cellspacing', 'colspan',
  'height', 'rowspan', 'scope', 'valign', 'width',
];

const FORBIDDEN_TAGS = [
  'audio', 'base', 'button', 'canvas', 'embed', 'form', 'iframe', 'input', 'link',
  'math', 'meta', 'object', 'option', 'script', 'select', 'source',
  'svg', 'template', 'textarea', 'track', 'video',
];

const ALLOWED_STYLE_PROPERTIES = new Set([
  'color', 'background-color', 'font-weight', 'font-style', 'font-size',
  'text-align', 'text-decoration', 'line-height', 'padding', 'padding-top',
  'padding-right', 'padding-bottom', 'padding-left', 'margin', 'margin-top',
  'margin-right', 'margin-bottom', 'margin-left', 'border', 'border-color',
  'border-width', 'border-style', 'border-radius', 'width', 'height',
  'vertical-align', 'white-space',
]);

const DANGEROUS_STYLE_PATTERNS = [
  /url\s*\(/i,
  /expression\s*\(/i,
  /javascript\s*:/i,
  /-moz-binding/i,
  /behavior\s*:/i,
  /@\s*import/i,
  /position\s*:\s*(fixed|absolute)/i,
  /--/,
  /var\(/,
  /\\/,
];

const sanitizeStyleAttributeValue = (rawValue) => {
  if (typeof rawValue !== 'string' || !rawValue) {
    return '';
  }

  if (DANGEROUS_STYLE_PATTERNS.some((pattern) => pattern.test(rawValue))) {
    return '';
  }

  const safeDeclarations = rawValue
    .split(';')
    .map((chunk) => chunk.trim())
    .filter(Boolean)
    .reduce((acc, chunk) => {
      const separatorIndex = chunk.indexOf(':');
      if (separatorIndex === -1) {
        return acc;
      }

      const property = chunk.slice(0, separatorIndex).trim().toLowerCase();
      const value = chunk.slice(separatorIndex + 1).trim();

      if (!value || !ALLOWED_STYLE_PROPERTIES.has(property)) {
        return acc;
      }

      acc.push(`${property}: ${value}`);
      return acc;
    }, []);

  return safeDeclarations.join('; ');
};

const WEB_URL_PATTERN = /^https?:$/i;
const HTTP_URL_PATTERN = /^https?:/i;

const isSafeHttpUrl = (value) => {
  if (typeof value !== 'string' || !value || value !== value.trim()) {
    return false;
  }

  try {
    return WEB_URL_PATTERN.test(new URL(value).protocol);
  } catch {
    return false;
  }
};

const STYLE_RULE_PATTERN = /([^{}]+)\{([^{}]*)\}/g;

const CSS_COMMENT_PATTERN = /\/\*[\s\S]*?\*\//g;

const STYLE_BLOCK_REJECT_PATTERN = /[@\\<]|url|expression|behaviou?r|binding|javascript|vbscript/i;

const sanitizeStyleBlockContent = (rawCss) => {
  if (typeof rawCss !== 'string' || !rawCss.trim()) {
    return '';
  }

  const screenedCss = rawCss.replace(CSS_COMMENT_PATTERN, ' ');

  const fusedCss = rawCss.replace(CSS_COMMENT_PATTERN, '');

  if (STYLE_BLOCK_REJECT_PATTERN.test(screenedCss) || STYLE_BLOCK_REJECT_PATTERN.test(fusedCss)) {
    return '';
  }

  let sanitizedCss = '';
  let match;
  STYLE_RULE_PATTERN.lastIndex = 0;

  while ((match = STYLE_RULE_PATTERN.exec(screenedCss)) !== null) {
    const [, rawSelector, rawDeclarations] = match;

    if (DANGEROUS_STYLE_PATTERNS.some((pattern) => pattern.test(rawSelector))) {
      continue;
    }

    const safeDeclarations = rawDeclarations
      .split(';')
      .filter((chunk) => {
        const trimmed = chunk.trim();
        if (!trimmed) {
          return false;
        }

        const separatorIndex = trimmed.indexOf(':');
        if (separatorIndex === -1) {
          return false;
        }

        const property = trimmed.slice(0, separatorIndex).trim().toLowerCase();
        const value = trimmed.slice(separatorIndex + 1).trim();

        if (!value || !ALLOWED_STYLE_PROPERTIES.has(property)) {
          return false;
        }

        return !DANGEROUS_STYLE_PATTERNS.some((pattern) => pattern.test(trimmed));
      });

    if (safeDeclarations.length > 0) {
      sanitizedCss += `${rawSelector}{${safeDeclarations.join(';')}}`;
    }
  }

  return sanitizedCss;
};

DOMPurify.addHook('uponSanitizeAttribute', (currentNode, hookEvent) => {
  if (
    currentNode.nodeName === 'IMG'
    && hookEvent.attrName === 'src'
    && !isSafeHttpUrl(hookEvent.attrValue)
  ) {
    hookEvent.keepAttr = false;
  }

  if (hookEvent.attrName === 'style') {
    const sanitizedStyleValue = sanitizeStyleAttributeValue(hookEvent.attrValue);

    if (!sanitizedStyleValue) {
      hookEvent.keepAttr = false;
    } else {
      hookEvent.attrValue = sanitizedStyleValue;
    }
  }
});

DOMPurify.addHook('uponSanitizeElement', (currentNode, hookEvent) => {
  if (hookEvent.tagName === 'style') {
    currentNode.textContent = sanitizeStyleBlockContent(currentNode.textContent);
  }
});

const hardenSanitizedLinks = (sanitizedHtml) => {
  const template = document.createElement('template');
  template.innerHTML = sanitizedHtml;

  template.content.querySelectorAll('a').forEach((anchor) => {
    const href = anchor.getAttribute('href');

    if (!isSafeHttpUrl(href)) {
      anchor.replaceWith(document.createTextNode(anchor.textContent || ''));
      return;
    }

    anchor.setAttribute('target', '_blank');
    anchor.setAttribute('rel', 'noopener noreferrer');
  });

  return template.innerHTML;
};

const liftDocumentStyles = (html) => {
  if (typeof DOMParser === 'undefined') {
    return html;
  }

  try {
    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    const { body, head } = parsedDocument;

    if (!body) {
      return html;
    }

    if (head) {
      const headStyles = Array.from(head.querySelectorAll('style'));

      for (let index = headStyles.length - 1; index >= 0; index -= 1) {
        body.insertBefore(headStyles[index], body.firstChild);
      }
    }

    return `<body>${body.innerHTML}</body>`;
  } catch {
    return html;
  }
};

export const sanitizeEmailHtml = (html) => {
  if (html === null || html === undefined) {
    return '';
  }

  try {
    const input = String(html);
    if (!input.trim() || DOMPurify.isSupported === false || typeof document === 'undefined') {
      return '';
    }

    const sanitizedHtml = DOMPurify.sanitize(liftDocumentStyles(input), {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      ADD_URI_SAFE_ATTR: URI_SAFE_ATTR,
      FORBID_TAGS: FORBIDDEN_TAGS,
      FORBID_ATTR: ['data-*', 'id', 'name', 'srcset'],
      ALLOW_ARIA_ATTR: false,
      ALLOW_DATA_ATTR: false,
      ALLOW_UNKNOWN_PROTOCOLS: false,
      ALLOWED_URI_REGEXP: HTTP_URL_PATTERN,
      RETURN_TRUSTED_TYPE: false,
    });

    return hardenSanitizedLinks(sanitizedHtml).trim();
  } catch {
    return '';
  }
};

export const hasRenderableEmailHtml = (sanitizedHtml) => {
  if (typeof sanitizedHtml !== 'string' || !sanitizedHtml.trim() || typeof document === 'undefined') {
    return false;
  }

  try {
    const template = document.createElement('template');
    template.innerHTML = sanitizedHtml;

    template.content.querySelectorAll('style').forEach((styleNode) => {
      styleNode.remove();
    });

    return Boolean(
      template.content.textContent.trim()
      || template.content.querySelector('img, hr, table')
    );
  } catch {
    return false;
  }
};

const LOOPBACK_HOSTNAMES = new Set(['localhost', '127.0.0.1', '[::1]', '::1']);

export const isAllowedMailWebLink = (webLink) => {
  if (!isSafeHttpUrl(webLink)) {
    return false;
  }

  const { protocol, hostname } = new URL(webLink);

  return protocol.toLowerCase() === 'https:' || LOOPBACK_HOSTNAMES.has(hostname.toLowerCase());
};
